/**
 * Marketing Banner Duck
 * This file contains the Redux logic for the marketing banner feature.
 *
 * gets initial state and reducers for the marketing banner
 * displays the marketing banner
 * updates state when user clicks the close button
 * handles showing and hiding the banner
 * provides a selector for the marketing banner state
 * exports the marketing banner actions and reducer
 *
 */

import { call, put, takeLatest } from 'redux-saga/effects';
import { getMarketingBanner } from '../utils/apiServices';
import { setError, clearError, setMaintenance } from './error';

/** ***********************************************************************
 *  Action Types
 *  *********************************************************************** */
export const FETCH_MARKETING_BANNER = 'marketing/FETCH_MARKETING_BANNER';
export const SHOW_MARKETING_BANNER = 'marketing/SHOW_MARKETING_BANNER';
export const HIDE_MARKETING_BANNER = 'marketing/HIDE_MARKETING_BANNER';
export const UPDATE_MARKETING_BANNER = 'marketing/UPDATE_MARKETING_BANNER';
export const START_MARKETING_SUBSCRIPTION =
  'marketing/START_MARKETING_SUBSCRIPTION';

/** ***********************************************************************
 *  Action Creators
 *  *********************************************************************** */
export const fetchMarketingBanner = () => ({
  type: FETCH_MARKETING_BANNER,
});

export const showMarketingBanner = (banner) => ({
  type: SHOW_MARKETING_BANNER,
  payload: banner,
});

export const hideMarketingBanner = () => ({
  type: HIDE_MARKETING_BANNER,
});

export const updateMarketingBanner = (banner) => ({
  type: UPDATE_MARKETING_BANNER,
  payload: banner,
});

export const startMarketingSubscription = () => ({
  type: START_MARKETING_SUBSCRIPTION,
});

/** ***********************************************************************
 *  Reducer
 *  *********************************************************************** */
const initialState = {
  banner: null,
  isVisible: false,
  isLoading: false,
};

const reducer = (state = initialState, action) => {
  switch (action.type) {
    case FETCH_MARKETING_BANNER:
      return {
        ...state,
        isLoading: true,
      };
    case SHOW_MARKETING_BANNER:
      return {
        ...state,
        banner: action.payload,
        isVisible: true,
        isLoading: false,
      };
    case HIDE_MARKETING_BANNER:
      return {
        ...state,
        isVisible: false,
      };
    case UPDATE_MARKETING_BANNER:
      return {
        ...state,
        banner: action.payload,
        isVisible: true,
      };
    default:
      return state;
  }
};

/** ***********************************************************************
 *  Sagas
 *  *********************************************************************** */
function* fetchMarketingBannerSaga() {
  try {
    const response = yield call(getMarketingBanner);

    if (response && response.success && response.banner) {
      // If backend indicates maintenance mode, surface a global error so Root
      // renders the GenericError/error page instead of the marketing banner.
      if (response.banner.maintenanceMode) {
        const message = response.banner.message;
        yield put(setMaintenance(message, true));
      } else {
        yield put(clearError());
      }

      const action = showMarketingBanner(response.banner);
      yield put(action);
    }

    // Start the AppSync subscription after initial fetch is complete
    yield put(startMarketingSubscription());
  } catch (error) {
    console.error('Failed to fetch marketing banner:', error);
    // Even if initial fetch fails, start the subscription for future updates
    yield put(startMarketingSubscription());
  }
}

function* setupSagas() {
  yield takeLatest(FETCH_MARKETING_BANNER, fetchMarketingBannerSaga);
}

/** ***********************************************************************
 *  Main Export
 *  *********************************************************************** */
export default {
  reducer: reducer,
  saga: setupSagas,
};
